import { MaxLengthValidatorDirective } from '../../shared/directives/max-length-validator.directive';
import { matchesNestedKeyword } from '../../shared/utils/keyword-filter';
import { BusinessClock } from '../../core/time/business-clock.service';
import { clockView } from '../../core/time/business-time';
import { JstDate } from '../../core/time/business-time';
import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { AuthStateService } from '../../core/services/auth-state.service';
import { StudioService } from '../../core/services/studio.service';
import { MemberService } from '../../core/services/member.service';
import { NotificationService } from '../../core/services/notification.service';
import { StudioSelectionService } from '../../core/services/studio-selection.service';
import { PagerComponent } from '../../shared/components/pager.component';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';
import { environment } from '../../../environments/environment';
import { isWithinJstDateRange, jstDateString } from '../../shared/utils/jst-date';
import { MemberStatusWorkflow } from './application/member-status.workflow';
import { MemberStatusCommand, MemberStatusOperation, MemberStatusTarget, statusBlocked, statusBusy } from './domain/member-status';
import { MemberStatusDialogComponent } from './ui/member-status-dialog.component';

const SESSION_KEY = 'member_select_studio_id';
const DATE_MIN = '2015-09-01';

function today(): string {
  return jstDateString();
}

@Component({
    selector: 'app-member-list',
    imports: [MaxLengthValidatorDirective, RouterLink, FormsModule, DatePipe, DecimalPipe, PagerComponent, FlashMessageComponent, MemberStatusDialogComponent],
    templateUrl: './member-list.component.html'
})
export class MemberListComponent implements OnInit, OnDestroy {
  private readonly businessClock = inject(BusinessClock);
  private readonly clockContext = clockView(true);

  productPriceWithTax(product: any): number {
    // 旧版 admin/js/controllers/member.js:386 と同じ「本体 + 切り捨てた税額」で算出する
    const rate = parseFloat(product.taxRate?.rate ?? '0');
    return product.amount + Math.floor(product.amount * rate);
  }

  private pageTitleService = inject(PageTitleService);
  private authState = inject(AuthStateService);
  private studioService = inject(StudioService);
  private memberService = inject(MemberService);
  private notificationService = inject(NotificationService);
  private studioSelection = inject(StudioSelectionService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private flash = inject(FlashService);

  readonly statusWorkflow = inject(MemberStatusWorkflow);
  statusTarget: MemberStatusTarget | null = null;
  statusTrigger?: HTMLElement;
  private statusSession: string | null = null;
  get activeStatusTarget(): MemberStatusTarget | null { return this.statusSession === this.authState.token ? this.statusTarget : null; }
  statusOutcomeChecked: Record<number, boolean> = {};
  private alive = true;
  private memberReadVersion = 0;
  ngOnDestroy(): void { this.alive = false; this.memberReadVersion++; this.clearTransactionMessage(); }
  get pendingStatusOperations(): MemberStatusOperation[] { return this.statusWorkflow.forStudio(this.selectedStudio?.id); }
  statusBusy = statusBusy;

  get userPermission(): number { return this.authState.currentUser?.role ?? 99; }

  studios: any[] = [];
  selectedStudio: any = null;
  items: any[] = [];
  searching = false;
  unprocessedExists = false;
  insuranceProduct: any = null;

  // Filter values
  keyword = '';
  studioPlanFilter: any = null;
  paymentMethodFilter: any = null;
  paymentStatusFilter: any = null;
  statusFilter: any = null;
  dateFrom = DATE_MIN;
  dateTo = today();
  enableDateFrom = '';
  enableDateTo = '';

  // Filter options
  paymentMethodOption = [
    { id: '0', name: '口座振替' },
    { id: '1', name: 'クレジットカード' },
  ];
  paymentStatusOption = [
    { id: '0', name: '有効' },
    { id: '1', name: '利用不可' },
  ];
  statusOption = [
    { id: '6', name: '見学予約' },
    { id: '5', name: '来館アンケート回答' },
    { id: '4', name: '仮登録' },
    { id: '0', name: '在籍中' },
    { id: '1', name: '停止' },
    { id: '2', name: '停止(除名)' },
  ];

  // Pagination
  readonly pageLimit = 300;
  pageIndex = 0;

  // Modal state
  showMemberDetailModal = false;
  selectedMember: any = null;

  showNoteModal = false;
  showAlertNoteModal = false;
  selectedMemberIdForNote: number | null = null;
  selectedMemberNote = '';

  showClearStartDateModal = false;
  clearStartDateMemberId: number | null = null;

  showPaymentModal = false;
  selectedPayment: any = null;
  selectedGmo: any = null;
  selectedMemberIdForPayment: number | null = null;

  showDownloadAllModal = false;
  showDownloadModal = false;

  // MM-17: 売上新規作成モーダル
  chargeTypeOption = [
    { id: 1, name: '月会費' },
    { id: 2, name: '入会金・事務手数料' },
    { id: 3, name: 'ロッカー費' },
    { id: 4, name: 'PT費' },
    { id: 5, name: '物販' },
    { id: 0, name: 'その他' },
  ];
  showTransactionModal = false;
  memberForTransaction: any = null;
  transactionProducts: any[] = [];
  inTheInput = true;
  transaction: any = {};
  // 旧版 Flash.create('success', 'データを作成しました', 5000, true) 相当（売上新規作成modal内）
  transactionMessage: string | null = null;
  private transactionMessageTimer?: ReturnType<typeof setTimeout>;

  studioPlansData: any[] = [];

  get studioPlans(): any[] {
    return this.studioPlansData;
  }

  get showFilterOption(): boolean {
    return !!this.selectedStudio;
  }

  get filteredItems(): any[] {
    let list = this.items;

    list = list.filter(item => matchesNestedKeyword(item, this.keyword));

    if (this.statusFilter?.id != null) {
      const sid = String(this.statusFilter.id);
      list = list.filter(m => String(m.status) === sid);
    }

    if (this.paymentMethodFilter?.id != null) {
      const pid = String(this.paymentMethodFilter.id);
      list = list.filter(m => String(m.payment?.paymentMethod) === pid);
    }

    if (this.paymentStatusFilter?.id != null) {
      const psi = String(this.paymentStatusFilter.id);
      list = list.filter(m => String(m.payment?.status) === psi);
    }

    if (this.studioPlanFilter?.id != null) {
      list = list.filter(m => m.studioPlanId === this.studioPlanFilter.id);
    }

    list = list.filter(m => isWithinJstDateRange(m.createdAt, this.dateFrom, this.dateTo));

    if (this.enableDateFrom || this.enableDateTo) {
      list = list.filter(m => isWithinJstDateRange(m.enabledAt, this.enableDateFrom, this.enableDateTo));
    }

    return list;
  }

  get pagedItems(): any[] {
    const start = this.pageIndex * this.pageLimit;
    return this.filteredItems.slice(start, start + this.pageLimit);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredItems.length / this.pageLimit);
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('会員管理', 's-icon-members');
    this.studios = await this.studioService.list('all') ?? [];
    this.notificationService.unprocessedExists().then(r => { this.unprocessedExists = !!r; });

    const restored = this.studioSelection.restore(this.studios, SESSION_KEY);
    if (restored) {
      this.selectedStudio = restored;
      this.statusWorkflow.enterStudio(restored.id);
      this.studioService.getInsuranceProduct(restored.id).then(r => { this.insuranceProduct = r; });
      this.studioService.list(restored.id).then((r: any) => { this.studioPlansData = r?.studioPlans ?? []; });
      await this.loadMembers();
    }
  }

  async onStudioChange(): Promise<void> {
    this.memberReadVersion++;
    this.searching = false;
    this.statusTarget = null;
    this.statusOutcomeChecked = {};
    this.items = [];
    this.pageIndex = 0;
    this.enableDateFrom = '';
    this.enableDateTo = '';
    if (!this.selectedStudio) {
      this.studioSelection.clear(SESSION_KEY);
      this.insuranceProduct = null;
      this.studioPlansData = [];
      return;
    }
    this.studioSelection.save(this.selectedStudio, SESSION_KEY);
    this.statusWorkflow.enterStudio(this.selectedStudio.id);
    this.studioService.getInsuranceProduct(this.selectedStudio.id).then(r => { this.insuranceProduct = r; });
    this.studioPlanFilter = null;
    this.studioService.list(this.selectedStudio.id).then((r: any) => { this.studioPlansData = r?.studioPlans ?? []; });
    await this.loadMembers();
  }

  private async loadMembers(): Promise<void> {
    if (!this.selectedStudio) return;
    const studioId = this.selectedStudio.id;
    const version = ++this.memberReadVersion;
    const token = this.authState.token;
    this.searching = true;
    try {
      const res = await this.studioService.getMember(studioId);
      if (this.alive && version === this.memberReadVersion && this.selectedStudio?.id === studioId && token === this.authState.token) this.items = res ?? [];
    } finally {
      if (this.alive && version === this.memberReadVersion) { this.searching = false; this.pageIndex = 0; }
    }
  }

  clearFilter(): void {
    this.keyword = '';
    this.studioPlanFilter = null;
    this.paymentMethodFilter = null;
    this.paymentStatusFilter = null;
    this.statusFilter = null;
    this.dateFrom = DATE_MIN;
    this.dateTo = today();
    this.enableDateFrom = '';
    this.enableDateTo = '';
    this.pageIndex = 0;
  }

  onKeywordChange(): void { this.pageIndex = 0; }

  goToPage(i: number): void {
    if (i >= 0 && i < this.totalPages) this.pageIndex = i;
  }

  // MM-13: 新規作成 — opens member app in new window (in-studio registration)
  openNewMember(): void {
    if (!this.selectedStudio) return;
    window.open(`${environment.memberUrl}/in_studio/${this.selectedStudio.id}/${encodeURIComponent(this.selectedStudio.name)}`);
  }

  // MM-16: 来館アンケート — opens member app visitors flow
  openVisitors(): void {
    if (!this.selectedStudio) return;
    window.open(`${environment.memberUrl}/visitors/${this.selectedStudio.id}/${encodeURIComponent(this.selectedStudio.name)}`);
  }

  async register(item: any): Promise<void> {
    const win = window.open('', '_blank');
    try {
      const tempPass = await this.memberService.createTempMemberPassword({ memberId: item.id });
      const studio = item.studio ?? this.selectedStudio;
      const studioId = studio?.id ?? item.studioId;
      if (!tempPass || !studioId) {
        win?.close();
        return;
      }

      const studioName = studio?.name ?? item.studioName ?? '';
      const url = `${environment.memberUrl}/register/${studioId}/${encodeURIComponent(studioName)}/${item.id}/${encodeURIComponent(String(tempPass))}`;
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      win?.close();
    }
  }

  openNoteModal(item: any): void {
    this.selectedMemberIdForNote = item.id;
    this.selectedMemberNote = item.note ?? '';
    if (item.isExpulsionAlert === 1) {
      this.showAlertNoteModal = true;
    } else {
      this.showNoteModal = true;
    }
  }

  closeNoteModal(): void {
    this.showNoteModal = false;
    this.showAlertNoteModal = false;
    this.selectedMemberIdForNote = null;
    this.selectedMemberNote = '';
  }

  async solveNote(): Promise<void> {
    if (!this.selectedMemberIdForNote) return;
    await this.memberService.upsert({ id: this.selectedMemberIdForNote, isExpulsionAlert: 0 });
    this.closeNoteModal();
    await this.loadMembers();
  }

  openMemberStatusModal(memberId: number, currentStatus: number, remove: boolean, event?: Event): void {
    if (!this.selectedStudio || this.statusWorkflow.recoveryError) return;
    this.statusWorkflow.enterStudio(this.selectedStudio.id);
    this.statusTarget = { memberId, studioId: this.selectedStudio.id, currentStatus, remove };
    this.statusSession = this.authState.token;
    this.statusTrigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : undefined;
  }

  closeMemberStatusModal(): void {
    if (this.statusTarget && statusBusy(this.statusWorkflow.operation(this.statusTarget.studioId, this.statusTarget.memberId))) return;
    this.statusTarget = null;
  }

  async applyMemberStatus(command: MemberStatusCommand): Promise<void> {
    const target = this.activeStatusTarget;
    if (!target || statusBlocked(this.statusWorkflow.operation(target.studioId, target.memberId))) return;
    const token = this.authState.token;
    const version = ++this.memberReadVersion;
    this.searching = false;
    const result = await this.statusWorkflow.execute(target, command);
    if (!this.alive || token !== this.authState.token || this.selectedStudio?.id !== target.studioId || this.statusTarget !== target) return;
    if (result.members && version === this.memberReadVersion) { this.items = result.members; this.pageIndex = 0; }
    if (result.completed) { this.statusTarget = null; this.flash.create('success', '状態を変更しました。'); }
  }

  async refreshMemberStatus(operation: MemberStatusOperation): Promise<void> {
    const token = this.authState.token;
    const version = ++this.memberReadVersion;
    this.searching = false;
    const members = await this.statusWorkflow.refresh(operation);
    if (members && this.alive && version === this.memberReadVersion && token === this.authState.token && this.selectedStudio?.id === operation.studioId) {
      this.items = members; this.pageIndex = 0;
    }
  }
  acknowledgeMemberStatus(operation: MemberStatusOperation): void {
    if (this.statusOutcomeChecked[operation.memberId] && this.statusWorkflow.acknowledge(operation)) {
      delete this.statusOutcomeChecked[operation.memberId];
      this.statusTarget = null;
    }
  }

  openClearStartDateModal(memberId: number): void {
    this.clearStartDateMemberId = memberId;
    this.showClearStartDateModal = true;
  }

  closeClearStartDateModal(): void {
    this.clearStartDateMemberId = null;
    this.showClearStartDateModal = false;
  }

  async clearStartDate(): Promise<void> {
    if (!this.clearStartDateMemberId) return;
    await this.memberService.upsert({ id: this.clearStartDateMemberId, desiredStartDate: null });
    this.closeClearStartDateModal();
    await this.loadMembers();
  }

  // MM-11: 会員氏名 detail modal
  setSelectMember(item: any): void {
    this.selectedMember = item;
    this.showMemberDetailModal = true;
  }

  // MM-12: 支払方法の確認
  async getPayment(memberId: number): Promise<void> {
    this.selectedPayment = null;
    this.selectedGmo = null;
    this.selectedMemberIdForPayment = memberId;
    const data: any = await this.memberService.getPayment(memberId);
    if (!data) return;
    this.selectedPayment = data.payment ?? {};
    this.selectedPayment.memberCode = data.memberCode;
    this.selectedPayment.paymentMethod = String(this.selectedPayment.paymentMethod ?? '');
    this.selectedGmo = {};
    if (data.gmo) {
      this.selectedGmo = data.gmo;
      this.selectedGmo.expireYearAt = String(data.gmo.Expire ?? '').substring(0, 2);
      this.selectedGmo.expireMonthAt = String(data.gmo.Expire ?? '').substring(2);
      this.selectedGmo.security = '****';
    }
    this.showPaymentModal = true;
  }

  notificationPayment(): void {
    if (!this.selectedMemberIdForPayment) return;
    this.showPaymentModal = false;
    this.router.navigate(['/notification', this.selectedMemberIdForPayment, 'payment']);
  }

  // MM-14: データ作成(全店一括)
  async downloadCsvAll(): Promise<void> {
    this.showDownloadAllModal = false;
    // 旧版 member.js:338-352: Flash.create(error ? 'danger' : 'success', error || 'データ作成を開始しました。', 5000, true)
    const error = await this.studioService.createMemberCsv('all');
    this.flash.create(error ? 'danger' : 'success', error || 'データ作成を開始しました。');
  }

  // MM-15: データ作成
  async downloadCsv(): Promise<void> {
    this.showDownloadModal = false;
    if (!this.selectedStudio) return;
    const error = await this.studioService.createMemberCsv(this.selectedStudio.id);
    this.flash.create(error ? 'danger' : 'success', error || 'データ作成を開始しました。');
  }

  // MM-17: 売上新規作成
  get filteredTransactionProducts(): any[] {
    if (this.transaction.chargeType == null) return [];
    return this.transactionProducts.filter(p => p.productType === this.transaction.chargeType);
  }

  get transactionTotal(): number {
    const p = this.transaction.product;
    if (!p) return 0;
    const rate = parseFloat(p.taxRate?.rate ?? '0');
    // saveTransaction が保存する taxIncluded と同じ式に合わせる
    return p.amount + Math.floor(p.amount * rate) + (this.transaction.adjustment || 0);
  }

  async openCreateTransaction(item: any): Promise<void> {
    const ym = this.defaultYearMonth();
    this.transaction = { chargeType: null, product: null, title: '', note: '', adjustment: null, yearPay: ym.year, monthPay: ym.month };
    this.inTheInput = true;
    this.clearTransactionMessage();
    this.memberForTransaction = null;
    this.transactionProducts = [];
    this.showTransactionModal = true;
    const memberData: any = await this.memberService.getOne(item.id);
    this.memberForTransaction = memberData;
    const products: any[] = (await this.studioService.getProduct(memberData.studio.id)) ?? [];
    this.transactionProducts = products.filter((p: any) => p.status === 0);
  }

  onTransactionChargeTypeChange(): void {
    const ct = this.chargeTypeOption.find(c => c.id === this.transaction.chargeType);
    this.transaction.title = ct ? `${this.transaction.yearPay}年${this.transaction.monthPay}月分 ${ct.name}` : '';
    this.transaction.product = null;
  }

  get isTransactionValid(): boolean {
    const t = this.transaction;
    if (t.chargeType == null || !t.product || !t.title || !this.memberForTransaction) return false;
    const adjustment = t.adjustment == null || t.adjustment === '' ? 0 : Number(t.adjustment);
    const baseAmount = this.productPriceWithTax(t.product);
    return Number.isInteger(adjustment) && adjustment >= -baseAmount && adjustment <= 99999
      && Number.isFinite(baseAmount) && String(t.note ?? '').length <= 200;
  }

  toggleInTheInput(): void {
    if (this.inTheInput && !this.isTransactionValid) return;
    this.inTheInput = !this.inTheInput;
  }

  async saveTransaction(): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    if (!this.isTransactionValid) return;
    const member = this.memberForTransaction;
    const p = this.transaction.product;
    const rate = parseFloat(p.taxRate?.rate ?? '0');
    const taxIncluded = p.amount + Math.floor(p.amount * rate) + (this.transaction.adjustment || 0);
    const taxExcluded = Math.ceil(taxIncluded / (1 + rate));
    const json: any = {
      chargeType: this.transaction.chargeType,
      title: this.transaction.title,
      note: this.transaction.note,
      yearPay: this.transaction.yearPay,
      monthPay: this.transaction.monthPay,
      productId: p.id,
      taxAmount: taxIncluded - taxExcluded,
      amount: taxIncluded,
      memberId: member.id,
      studioId: member.studio.id,
      paymentMethod: member.payment?.paymentMethod,
      isAutoGenerate: 1,
    };
    await this.memberService.upsertTransaction(member.id, json);
    // 旧版 member.js:408-417: modalは閉じず入力画面へ戻し、modal内Flashを表示して売上タイプ・明細・備考をclear
    // （売上タイプ未選択では商品候補が空になるため商品も未選択へ戻す）
    this.inTheInput = true;
    this.transaction.chargeType = null;
    this.transaction.product = null;
    this.transaction.title = '';
    this.transaction.note = null;
    this.showTransactionMessage('データを作成しました');
  }

  clearTransactionMessage(): void {
    if (this.transactionMessageTimer !== undefined) clearTimeout(this.transactionMessageTimer);
    this.transactionMessageTimer = undefined;
    this.transactionMessage = null;
  }

  private showTransactionMessage(text: string): void {
    this.clearTransactionMessage();
    this.transactionMessage = text;
    this.transactionMessageTimer = this.zone.runOutsideAngular(() =>
      setTimeout(() => this.zone.run(() => this.clearTransactionMessage()), 5000));
  }

  chargeTypeName(id: number | null): string {
    return this.chargeTypeOption.find(c => c.id === id)?.name ?? '';
  }

  private defaultYearMonth(): { year: number; month: number } {
    const now = new JstDate();
    if (now.getDate() < 11) {
      return { year: now.getFullYear(), month: now.getMonth() + 1 };
    }
    const next = new JstDate(now.getFullYear(), now.getMonth() + 1, 1);
    return { year: next.getFullYear(), month: next.getMonth() + 1 };
  }
}
